import { useEffect, useState } from "react";
import { ShieldCheck, Database, FileText, UserCheck, AlertOctagon, FileCheck, Activity, AlertTriangle, CheckCircle2, Clock, Trash2, Eye, Download, GitBranch, Settings2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { fmtDateTime } from "@/lib/time";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";
import { useFeatures } from "@/context/FeaturesContext";
import ChangeManagementTab from "@/components/compliance/ChangeManagementTab";

const GoldButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground hover:bg-[#C9A537] disabled:opacity-40 text-xs font-medium uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);
const GhostButton = ({ children, ...p }) => (
    <button {...p} className={`inline-flex items-center gap-2 px-4 py-2 border border-border hover:border-primary hover:text-primary disabled:opacity-40 text-xs uppercase tracking-[0.15em] ${p.className || ""}`}>{children}</button>
);

const TONE = {
    green: { dot: "bg-emerald-400", border: "border-emerald-500/40", text: "text-emerald-400" },
    amber: { dot: "bg-amber-400", border: "border-amber-500/40", text: "text-amber-400" },
    red: { dot: "bg-red-500 animate-pulse", border: "border-red-500/60", text: "text-red-400" },
};

const ENTITY_OPTIONS = [
    { value: "cases", label: "Litigations" },
    { value: "hearings", label: "Hearings" },
    { value: "tasks", label: "Tasks" },
    { value: "documents", label: "Documents" },
    { value: "communications", label: "Communications" },
    { value: "invoices", label: "Invoices" },
    { value: "audit_logs", label: "Audit logs" },
];

export default function Compliance() {
    const { flags } = useFeatures();
    // ISO 27001 add-on: platform admin enables per-tenant. Off by default —
    // hides Changes + ISO Config tabs entirely for tenants without the add-on.
    const isoEnabled = !!flags?.iso27001_addon;
    const [tab, setTab] = useState("posture");
    const [posture, setPosture] = useState(null);
    const [retention, setRetention] = useState([]);
    const [dsr, setDsr] = useState([]);
    const [incidents, setIncidents] = useState([]);
    const [consents, setConsents] = useState([]);
    const [loginEvents, setLoginEvents] = useState([]);
    const [alertEvents, setAlertEvents] = useState([]);

    const loadAll = async () => {
        try {
            const [p, r, d, i, c, le, ae] = await Promise.all([
                api.get("/compliance/posture"),
                api.get("/compliance/retention"),
                api.get("/compliance/dsr"),
                api.get("/compliance/incidents"),
                api.get("/compliance/consents"),
                api.get("/compliance/login-events?limit=50"),
                api.get("/compliance/alerts/events?limit=50"),
            ]);
            setPosture(p.data);
            setRetention(r.data);
            setDsr(d.data);
            setIncidents(i.data);
            setConsents(c.data);
            setLoginEvents(le.data);
            setAlertEvents(ae.data);
        } catch (e) { toast.error(apiError(e)); }
    };

    useEffect(() => { loadAll(); }, []);

    if (!posture) return null;

    return (
        <div className="space-y-6" data-testid="compliance-page">
            <div>
                <div className="overline">Privacy & Security</div>
                <h1 className="display-display mt-1">Compliance Posture</h1>
                <p className="text-sm text-muted-foreground mt-2 max-w-3xl">
                    Active controls and audit-ready evidence under <span className="text-foreground">DPDP Act 2023</span>, <span className="text-foreground">IT Act 2000 §43A</span> and GDPR-compatible clauses. Every action you take here writes to the firm's audit log.
                </p>
            </div>

            <OverallStatusCard posture={posture}/>

            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {posture.pillars.map(p => <PillarCard key={p.key} p={p} onClick={() => setTab(p.key)}/>)}
            </div>

            <div className="flex gap-1 border-b border-border overflow-x-auto">
                {[
                    ["posture", "Overview", ShieldCheck],
                    ["retention", "Retention", Database],
                    ["dpa", "DPA", FileCheck],
                    ["dsr", "Subject Requests", UserCheck],
                    ["incidents", "Incidents", AlertOctagon],
                    ...(isoEnabled ? [
                        ["changes", "Changes", GitBranch],
                        ["iso-config", "ISO Config", Settings2],
                    ] : []),
                    ["consents", "Consents", FileText],
                    ["suspicious", "Activity", Activity],
                ].map(([k, label, Icon]) => (
                    <button key={k} onClick={() => setTab(k)} data-testid={`compliance-tab-${k}`}
                        className={`flex items-center gap-1.5 px-3 py-3 text-[11px] uppercase tracking-[0.15em] whitespace-nowrap -mb-px border-b-2 transition-colors ${tab === k ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
                        <Icon className="w-3.5 h-3.5"/> {label}
                    </button>
                ))}
            </div>

            {tab === "posture" && <OverviewTab/>}
            {tab === "retention" && <RetentionTab rows={retention} onChange={loadAll}/>}
            {tab === "dpa" && <DPATab/>}
            {tab === "dsr" && <DSRTab rows={dsr} onChange={loadAll}/>}
            {tab === "incidents" && <IncidentsTab rows={incidents} onChange={loadAll}/>}
            {isoEnabled && tab === "changes" && <ChangeManagementTab scope="tenant"/>}
            {isoEnabled && tab === "iso-config" && <IsoConfigTab/>}
            {tab === "consents" && <ConsentsTab rows={consents} onChange={loadAll}/>}
            {tab === "suspicious" && <SuspiciousTab loginEvents={loginEvents} alertEvents={alertEvents} onChange={loadAll}/>}
        </div>
    );
}

function OverallStatusCard({ posture }) {
    const tone = TONE[posture.overall] || TONE.amber;
    const labels = { green: "All clear", amber: "Action recommended", red: "Immediate attention" };
    return (
        <div className={`bg-card border-2 ${tone.border} p-6 flex items-center gap-4`} data-testid="compliance-overall-card">
            <div className={`w-3 h-3 rounded-full ${tone.dot}`}/>
            <div className="flex-1">
                <div className={`text-xl font-serif ${tone.text}`} data-testid="compliance-overall-status">{labels[posture.overall]}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Framework: {posture.legal_framework.join(" · ")}</div>
            </div>
            <div className="text-[10px] text-muted-foreground">Last computed {fmtDateTime(posture.computed_at)}</div>
        </div>
    );
}

function PillarCard({ p, onClick }) {
    const tone = TONE[p.status] || TONE.amber;
    return (
        <button onClick={onClick} data-testid={`pillar-${p.key}`}
            className={`text-left bg-card border ${tone.border} p-5 hover:border-primary transition-colors`}>
            <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${tone.dot}`}/>
                <div className="overline">{p.label}</div>
            </div>
            <div className="text-xl font-serif text-foreground mt-2">{p.metric}</div>
            <div className="text-[11px] text-muted-foreground mt-1 leading-snug">{p.hint}</div>
        </button>
    );
}

function OverviewTab() {
    return (
        <div className="bg-card border border-border p-6 space-y-3" data-testid="overview-tab">
            <div className="overline">Your active controls</div>
            <ul className="space-y-2 text-sm text-muted-foreground">
                {[
                    "End-to-end encryption: AES-256 / Fernet at rest, TLS 1.2+ in transit",
                    "Strict multi-tenant isolation — no cross-firm data access possible",
                    "Mandatory MFA / Passkeys for privileged roles",
                    "Append-only audit log on every PATCH / DELETE with mandatory change reason",
                    "72-hour data breach notification SLA to Data Protection Board (DPDP §8(6))",
                    "Data Subject Request fulfilment within 15 days (DPDP §13)",
                    "Consent log (append-only, withdrawal-aware) per DPDP §6",
                    "Anti-abuse IP-based suspicious activity rules with audit trail",
                ].map((t) => (
                    <li key={t} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0"/>{t}</li>
                ))}
            </ul>
        </div>
    );
}

function RetentionTab({ rows, onChange }) {
    const [form, setForm] = useState({ entity: "communications", days: 365, only_status: "" });
    const [running, setRunning] = useState(false);
    const [preview, setPreview] = useState(null);
    const save = async () => {
        try { await api.post("/compliance/retention", { entity: form.entity, days: parseInt(form.days, 10), only_status: form.only_status || undefined }); toast.success("Policy saved"); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const remove = async (entity) => {
        if (!window.confirm(`Remove retention policy for ${entity}?`)) return;
        try { await api.delete(`/compliance/retention/${entity}`); toast.success("Removed"); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const runSweep = async (dryRun) => {
        if (!dryRun && !window.confirm("Permanently delete records past their retention window?")) return;
        setRunning(true);
        try { const { data } = await api.post(`/compliance/retention/run?dry_run=${dryRun}`); setPreview(data); toast.success(dryRun ? "Preview ready" : "Sweep complete"); onChange(); }
        catch (e) { toast.error(apiError(e)); }
        finally { setRunning(false); }
    };
    return (
        <div className="space-y-4" data-testid="retention-tab">
            <div className="bg-card border border-border p-6 space-y-3">
                <div className="overline">Add policy</div>
                <p className="text-xs text-muted-foreground leading-relaxed">
                    Sets max-age per data type. Older rows are hard-deleted on sweep. Note: Bar Council and Limitation Act may require longer retention for case-related data — consult your DPO before lowering.
                </p>
                <div className="grid sm:grid-cols-4 gap-3">
                    <Field label="Data type">
                        <select value={form.entity} onChange={e => setForm({ ...form, entity: e.target.value })} data-testid="retention-entity-select"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm">
                            {ENTITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Max age (days)"><Input type="number" value={form.days} onChange={e => setForm({ ...form, days: e.target.value })} data-testid="retention-days-input"/></Field>
                    <Field label="Only when status equals"><Input value={form.only_status} onChange={e => setForm({ ...form, only_status: e.target.value })} placeholder="e.g. closed" data-testid="retention-status-input"/></Field>
                    <div className="flex items-end"><GoldButton onClick={save} data-testid="retention-save-btn">Save policy</GoldButton></div>
                </div>
            </div>
            <div className="bg-card border border-border p-6 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="overline">Active policies</div>
                    <div className="flex gap-2">
                        <GhostButton onClick={() => runSweep(true)} disabled={running} data-testid="retention-preview-btn"><Eye className="w-3.5 h-3.5"/>Preview</GhostButton>
                        <GhostButton onClick={() => runSweep(false)} disabled={running} data-testid="retention-run-btn"><Trash2 className="w-3.5 h-3.5"/>Run sweep</GhostButton>
                    </div>
                </div>
                {rows.length === 0 ? <p className="text-xs text-muted-foreground">No policies yet.</p>
                : <div className="space-y-2">{rows.map(r => (
                    <div key={r.id} className="flex items-center justify-between border border-border px-3 py-2" data-testid={`retention-row-${r.id}`}>
                        <div className="text-sm">
                            <span className="text-primary">{r.entity}</span>
                            <span className="text-muted-foreground ml-2">{r.days === 0 ? "Retain forever" : `Delete after ${r.days} days`}</span>
                            {r.only_status && <span className="text-muted-foreground ml-2 text-[10px]">(only status = {r.only_status})</span>}
                        </div>
                        <button onClick={() => remove(r.entity)} className="text-red-400 hover:text-red-300 text-xs" data-testid={`retention-delete-${r.entity}`}>Remove</button>
                    </div>
                ))}</div>}
                {preview && (
                    <div className="bg-background border border-border p-3 text-xs space-y-1 mt-3">
                        <div className="overline">Last sweep {preview.dry_run ? "(preview)" : ""} · {fmtDateTime(preview.ran_at)}</div>
                        {preview.summary.length === 0 ? <div className="text-muted-foreground">No matches.</div>
                        : preview.summary.map(s => <div key={s.entity}>{s.entity}: <span className="text-primary">{s.matched}</span> matched · <span className={s.deleted > 0 ? "text-red-400" : "text-muted-foreground"}>{s.deleted} deleted</span></div>)}
                    </div>
                )}
            </div>
        </div>
    );
}

function DPATab() {
    const [form, setForm] = useState({ client_name: "", client_address: "", dpo_email: "", purpose: "Legal services / case management" });
    const [busy, setBusy] = useState(false);
    const generate = async () => {
        if (!form.client_name.trim()) { toast.error("Client name required"); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/compliance/dpa/generate", form);
            const blob = b64ToBlob(data.pdf_base64, "application/pdf");
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a"); a.href = url; a.download = data.filename; a.click();
            URL.revokeObjectURL(url);
            toast.success("DPA generated");
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };
    return (
        <div className="bg-card border border-border p-6 space-y-4" data-testid="dpa-tab">
            <div>
                <div className="overline">Generate Data Processing Agreement</div>
                <p className="text-xs text-muted-foreground mt-2 leading-relaxed">
                    Tailored to DPDP Act 2023 with GDPR-compatible clauses. Edit placeholders in the PDF before signing.
                </p>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
                <Field label="Client / Data Controller name *"><Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} data-testid="dpa-client-name"/></Field>
                <Field label="Client address"><Input value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} data-testid="dpa-client-address"/></Field>
                <Field label="DPO email"><Input value={form.dpo_email} onChange={e => setForm({ ...form, dpo_email: e.target.value })} data-testid="dpa-dpo-email"/></Field>
                <Field label="Processing purpose"><Input value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} data-testid="dpa-purpose"/></Field>
            </div>
            <div className="flex justify-end"><GoldButton onClick={generate} disabled={busy} data-testid="dpa-generate-btn"><Download className="w-3.5 h-3.5"/>{busy ? "Generating…" : "Generate DPA"}</GoldButton></div>
        </div>
    );
}

const DSR_TYPES = [
    { value: "access", label: "Access (DPDP §11)" },
    { value: "correction", label: "Correction (DPDP §12)" },
    { value: "erasure", label: "Erasure (DPDP §12(3))" },
    { value: "portability", label: "Portability" },
    { value: "objection", label: "Objection" },
];

function DSRTab({ rows, onChange }) {
    const [form, setForm] = useState({ request_type: "access", data_principal_email: "", data_principal_name: "", description: "" });
    const tableCtrl = useTableControls(rows, {
        accessors: {
            principal: (r) => r.data_principal_name || r.data_principal_email,
            status: (r) => (r.status || "").replace("_", " "),
        },
    });
    const create = async () => {
        if (!form.data_principal_email.trim()) { toast.error("Email is required"); return; }
        try { await api.post("/compliance/dsr", form); toast.success("DSR logged"); setForm({ ...form, data_principal_email: "", data_principal_name: "", description: "" }); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const setStatus = async (id, status) => {
        try { await api.patch(`/compliance/dsr/${id}`, { status }); toast.success(`Status: ${status}`); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    return (
        <div className="space-y-4" data-testid="dsr-tab">
            <div className="bg-card border border-border p-6 space-y-3">
                <div className="overline">Log new request</div>
                <p className="text-xs text-muted-foreground">Statutory window: 15 days under DPDP §13. Countdown begins now.</p>
                <div className="grid sm:grid-cols-4 gap-3">
                    <Field label="Type">
                        <select value={form.request_type} onChange={e => setForm({ ...form, request_type: e.target.value })} data-testid="dsr-type-select"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm">
                            {DSR_TYPES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Name"><Input value={form.data_principal_name} onChange={e => setForm({ ...form, data_principal_name: e.target.value })} data-testid="dsr-name-input"/></Field>
                    <Field label="Email *"><Input value={form.data_principal_email} onChange={e => setForm({ ...form, data_principal_email: e.target.value })} data-testid="dsr-email-input"/></Field>
                    <div className="flex items-end"><GoldButton onClick={create} data-testid="dsr-create-btn">Log request</GoldButton></div>
                </div>
                <Field label="Description"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="dsr-desc-input"/></Field>
            </div>
            <div className="bg-card border border-border overflow-x-auto">
                <FilterChip ctrl={tableCtrl} className="m-3"/>
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                        <SortableTh field="request_type" ctrl={tableCtrl} className="py-3 px-4">Type</SortableTh>
                        <SortableTh field="principal" ctrl={tableCtrl} className="py-3 px-4">Principal</SortableTh>
                        <SortableTh field="deadline" ctrl={tableCtrl} className="py-3 px-4">Deadline</SortableTh>
                        <SortableTh field="status" ctrl={tableCtrl} className="py-3 px-4">Status</SortableTh>
                        <th className="py-3 px-4 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                        {tableCtrl.rows.length === 0 ? <tr><td colSpan="5" className="py-8 text-center text-xs text-muted-foreground">No requests yet.</td></tr>
                        : tableCtrl.rows.map(r => {
                            const overdue = r.status !== "fulfilled" && r.status !== "rejected" && r.deadline && new Date(r.deadline) < new Date();
                            return (
                                <tr key={r.id} className="border-b border-border" data-testid={`dsr-row-${r.id}`}>
                                    <td className="py-3 px-4 capitalize">{r.request_type}</td>
                                    <td className="py-3 px-4 text-foreground">{r.data_principal_name || r.data_principal_email}<div className="text-[10px] text-muted-foreground">{r.data_principal_email}</div></td>
                                    <td className={`py-3 px-4 text-xs ${overdue ? "text-red-400" : "text-muted-foreground"}`}>{fmtDateTime(r.deadline)}{overdue && " ⚠"}</td>
                                    <td className="py-3 px-4 capitalize">{r.status?.replace("_", " ")}</td>
                                    <td className="py-3 px-4 text-right">
                                        {r.status !== "fulfilled" && r.status !== "rejected" && (
                                            <div className="flex gap-3 justify-end text-[10px]">
                                                <button onClick={() => setStatus(r.id, "in_progress")} className="text-amber-400 hover:underline" data-testid={`dsr-progress-${r.id}`}>In progress</button>
                                                <button onClick={() => setStatus(r.id, "fulfilled")} className="text-emerald-400 hover:underline" data-testid={`dsr-fulfill-${r.id}`}>Fulfill</button>
                                                <button onClick={() => setStatus(r.id, "rejected")} className="text-red-400 hover:underline" data-testid={`dsr-reject-${r.id}`}>Reject</button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function IncidentsTab({ rows, onChange }) {
    const [form, setForm] = useState({ title: "", severity: "medium", description: "", affected_count: 0 });
    const create = async () => {
        if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description required"); return; }
        try { await api.post("/compliance/incidents", { ...form, affected_count: parseInt(form.affected_count, 10) || 0 }); toast.success("Incident logged — 72h DPB deadline started"); setForm({ title: "", severity: "medium", description: "", affected_count: 0 }); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const update = async (id, patch) => {
        try { await api.patch(`/compliance/incidents/${id}`, patch); toast.success("Incident updated"); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    return (
        <div className="space-y-4" data-testid="incidents-tab">
            <div className="bg-card border border-border p-6 space-y-3">
                <div className="overline">Report new incident</div>
                <p className="text-xs text-amber-400 flex gap-2 items-start"><Clock className="w-4 h-4 flex-shrink-0 mt-0.5"/>
                    DPDP §8(6) mandates notification to the Data Protection Board within <strong>72 hours</strong> of discovery.
                </p>
                <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Title *"><Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="incident-title-input"/></Field>
                    <Field label="Severity">
                        <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })} data-testid="incident-severity-select"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm">
                            {["low", "medium", "high", "critical"].map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    </Field>
                    <Field label="Affected count"><Input type="number" value={form.affected_count} onChange={e => setForm({ ...form, affected_count: e.target.value })} data-testid="incident-affected-input"/></Field>
                </div>
                <Field label="Description *"><Input value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="incident-desc-input"/></Field>
                <div className="flex justify-end"><GoldButton onClick={create} data-testid="incident-create-btn">Log incident</GoldButton></div>
            </div>
            {rows.length > 0 && (
                <div className="space-y-2">
                    {rows.map(r => {
                        const now = new Date();
                        const deadline = new Date(r.dpb_deadline);
                        const overdue = !r.dpb_notified_at && deadline < now;
                        const hoursLeft = Math.max(0, Math.floor((deadline - now) / 3600000));
                        const tone = r.status === "closed" ? "border-border" : overdue ? "border-red-500/60" : "border-amber-500/40";
                        return (
                            <div key={r.id} className={`bg-card border ${tone} p-5`} data-testid={`incident-row-${r.id}`}>
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <div className="text-foreground">{r.title}</div>
                                            <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${r.severity === "critical" ? "bg-red-500/20 text-red-400" : r.severity === "high" ? "bg-amber-500/20 text-amber-400" : "bg-background text-muted-foreground"}`}>{r.severity}</span>
                                            <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">· {r.status}</span>
                                        </div>
                                        <div className="text-xs text-muted-foreground mt-1">{r.description}</div>
                                        <div className="text-[10px] text-muted-foreground mt-2">
                                            Discovered {fmtDateTime(r.discovered_at)} · Affected: {r.affected_count}
                                            {r.dpb_notified_at ? <span className="text-emerald-400 ml-2">✓ DPB notified {fmtDateTime(r.dpb_notified_at)}</span>
                                                : overdue ? <span className="text-red-400 ml-2">⚠ DPB deadline passed</span>
                                                : <span className="text-amber-400 ml-2">⏱ {hoursLeft}h until DPB deadline</span>}
                                        </div>
                                    </div>
                                    <div className="flex gap-2 flex-wrap">
                                        {!r.dpb_notified_at && <GhostButton onClick={() => update(r.id, { dpb_notified_at: new Date().toISOString() })} data-testid={`incident-notify-${r.id}`}>Mark DPB notified</GhostButton>}
                                        <IncidentStageAdvance row={r} onAdvance={(next) => update(r.id, { status: next })}/>
                                    </div>
                                </div>
                                {/* ISO 27035 lifecycle strip */}
                                <IncidentLifecycleStrip row={r}/>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

// ---- ISO 27035 lifecycle helpers (used above) -------------------------------
const ISO27035_STAGES = ["reported", "assessed", "contained", "eradicated", "recovered", "closed"];
const _LEGACY = { open: "reported", investigating: "assessed" };
function _currentStage(row) {
    return _LEGACY[row.status] || row.status || "reported";
}

function IncidentLifecycleStrip({ row }) {
    const current = _currentStage(row);
    const idx = ISO27035_STAGES.indexOf(current);
    return (
        <div className="mt-4 pt-3 border-t border-border">
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-2">ISO 27035 lifecycle</div>
            <div className="flex items-center gap-1 overflow-x-auto" data-testid={`incident-lifecycle-${row.id}`}>
                {ISO27035_STAGES.map((stage, i) => {
                    const active = i <= idx;
                    const isCurrent = i === idx;
                    return (
                        <div key={stage} className="flex items-center gap-1 shrink-0">
                            <div className={`w-2 h-2 rounded-full ${active ? (isCurrent ? "bg-primary animate-pulse" : "bg-emerald-400") : "bg-muted-foreground/30"}`}/>
                            <span className={`text-[10px] uppercase tracking-[0.12em] ${active ? "text-foreground" : "text-muted-foreground/50"}`}>{stage}</span>
                            {i < ISO27035_STAGES.length - 1 && <div className={`w-4 h-px ${active ? "bg-emerald-400/50" : "bg-muted-foreground/20"}`}/>}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function IncidentStageAdvance({ row, onAdvance }) {
    const current = _currentStage(row);
    const idx = ISO27035_STAGES.indexOf(current);
    if (idx < 0 || idx >= ISO27035_STAGES.length - 1) return null;
    const next = ISO27035_STAGES[idx + 1];
    return (
        <GhostButton onClick={() => onAdvance(next)} data-testid={`incident-advance-${row.id}`}>
            Advance → {next}
        </GhostButton>
    );
}

// ---- ISO 27001 · per-tenant Config tab --------------------------------------
function IsoConfigTab() {
    const [cfg, setCfg] = useState(null);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.get("/iso27001/config").then(r => setCfg(r.data))
            .catch(e => toast.error(apiError(e)));
    }, []);
    if (!cfg) return null;
    const save = async () => {
        setSaving(true);
        try {
            await api.put("/iso27001/config", {
                dsr_sla_days: parseInt(cfg.dsr_sla_days, 10) || 30,
                incident_notify_emails: (cfg.incident_notify_emails || []).filter(Boolean),
            });
            toast.success("ISO 27001 configuration saved");
        } catch (e) { toast.error(apiError(e)); }
        finally { setSaving(false); }
    };
    const emails = (cfg.incident_notify_emails || []).join("\n");
    return (
        <div className="space-y-4" data-testid="iso-config-tab">
            <div className="bg-card border border-border p-6 space-y-4 max-w-2xl">
                <div>
                    <div className="overline">Data Subject Request SLA</div>
                    <p className="text-xs text-muted-foreground mt-1">Days to fulfil a DSR from receipt. DPDP Act 2023 mandates a maximum of 30 days.</p>
                    <input type="number" min={1} max={365} value={cfg.dsr_sla_days}
                        onChange={e => setCfg({ ...cfg, dsr_sla_days: e.target.value })}
                        data-testid="iso-config-dsr-sla"
                        className="mt-2 w-32 bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm"/>
                    <span className="ml-2 text-xs text-muted-foreground">days</span>
                </div>
                <div>
                    <div className="overline">Incident notification recipients</div>
                    <p className="text-xs text-muted-foreground mt-1">One email per line. These addresses are notified in addition to super-admins and the tenant owner whenever an incident is reported.</p>
                    <textarea rows={4} value={emails}
                        onChange={e => setCfg({ ...cfg, incident_notify_emails: e.target.value.split(/\n+/).map(s => s.trim()) })}
                        data-testid="iso-config-incident-emails"
                        placeholder="ciso@yourfirm.com&#10;security-oncall@yourfirm.com"
                        className="mt-2 w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm font-mono"/>
                </div>
                <div className="flex justify-end">
                    <GoldButton onClick={save} disabled={saving} data-testid="iso-config-save">
                        {saving ? "Saving…" : "Save configuration"}
                    </GoldButton>
                </div>
            </div>
        </div>
    );
}

function ConsentsTab({ rows, onChange }) {
    const [form, setForm] = useState({ data_principal_email: "", data_principal_name: "", purpose: "Case communication", channel: "email", granted: true, evidence: "" });
    const tableCtrl = useTableControls(rows, {
        accessors: {
            principal: (r) => r.data_principal_name || r.data_principal_email,
            status: (r) => r.granted ? "Active" : "Withdrawn",
        },
    });
    const create = async () => {
        if (!form.data_principal_email.trim() || !form.purpose.trim()) { toast.error("Email and purpose required"); return; }
        try { await api.post("/compliance/consents", form); toast.success("Consent recorded"); setForm({ ...form, data_principal_email: "", data_principal_name: "", evidence: "" }); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const withdraw = async (id) => {
        if (!window.confirm("Record consent withdrawal?")) return;
        try { await api.post(`/compliance/consents/${id}/withdraw`); toast.success("Withdrawal recorded"); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    return (
        <div className="space-y-4" data-testid="consents-tab">
            <div className="bg-card border border-border p-6 space-y-3">
                <div className="overline">Record new consent (DPDP §6)</div>
                <div className="grid sm:grid-cols-3 gap-3">
                    <Field label="Email *"><Input value={form.data_principal_email} onChange={e => setForm({ ...form, data_principal_email: e.target.value })} data-testid="consent-email-input"/></Field>
                    <Field label="Name"><Input value={form.data_principal_name} onChange={e => setForm({ ...form, data_principal_name: e.target.value })} data-testid="consent-name-input"/></Field>
                    <Field label="Purpose *"><Input value={form.purpose} onChange={e => setForm({ ...form, purpose: e.target.value })} data-testid="consent-purpose-input"/></Field>
                    <Field label="Channel">
                        <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })} data-testid="consent-channel-select"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm">
                            {["email", "in-person", "web-form", "phone"].map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Evidence URL"><Input value={form.evidence} onChange={e => setForm({ ...form, evidence: e.target.value })} data-testid="consent-evidence-input"/></Field>
                    <div className="flex items-end"><GoldButton onClick={create} data-testid="consent-create-btn">Record</GoldButton></div>
                </div>
            </div>
            <div className="bg-card border border-border overflow-x-auto">
                <FilterChip ctrl={tableCtrl} className="m-3"/>
                <table className="w-full text-sm">
                    <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                        <SortableTh field="principal" ctrl={tableCtrl} className="py-3 px-4">Principal</SortableTh>
                        <SortableTh field="purpose" ctrl={tableCtrl} className="py-3 px-4">Purpose</SortableTh>
                        <SortableTh field="channel" ctrl={tableCtrl} className="py-3 px-4">Channel</SortableTh>
                        <SortableTh field="recorded_at" ctrl={tableCtrl} className="py-3 px-4">Recorded</SortableTh>
                        <SortableTh field="status" ctrl={tableCtrl} className="py-3 px-4">Status</SortableTh>
                        <th className="py-3 px-4 text-right">Actions</th>
                    </tr></thead>
                    <tbody>
                        {tableCtrl.rows.length === 0 ? <tr><td colSpan="6" className="py-8 text-center text-xs text-muted-foreground">No consents recorded yet.</td></tr>
                        : tableCtrl.rows.map(r => (
                            <tr key={r.id} className="border-b border-border" data-testid={`consent-row-${r.id}`}>
                                <td className="py-3 px-4">{r.data_principal_name || r.data_principal_email}<div className="text-[10px] text-muted-foreground">{r.data_principal_email}</div></td>
                                <td className="py-3 px-4 text-muted-foreground text-xs">{r.purpose}</td>
                                <td className="py-3 px-4 text-muted-foreground text-xs">{r.channel}</td>
                                <td className="py-3 px-4 text-muted-foreground text-xs">{fmtDateTime(r.recorded_at)}</td>
                                <td className="py-3 px-4">{r.granted ? <span className="text-emerald-400 text-xs">● Active</span> : <span className="text-muted-foreground text-xs">○ Withdrawn</span>}</td>
                                <td className="py-3 px-4 text-right">
                                    {r.granted && !r.withdrawn_from && <button onClick={() => withdraw(r.id)} className="text-[10px] text-red-400 hover:underline" data-testid={`consent-withdraw-${r.id}`}>Withdraw</button>}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function SuspiciousTab({ loginEvents, alertEvents, onChange }) {
    const loginCtrl = useTableControls(loginEvents, {
        accessors: { result: (e) => e.success ? "success" : "failed" },
    });
    const resolveAlert = async (id) => {
        try { await api.post(`/compliance/alerts/events/${id}/resolve`); toast.success("Alert resolved"); onChange(); }
        catch (e) { toast.error(apiError(e)); }
    };
    return (
        <div className="space-y-4" data-testid="suspicious-tab">
            <div className="bg-card border border-border p-6 space-y-2">
                <div className="overline">Recent alerts</div>
                {alertEvents.length === 0 ? <p className="text-xs text-muted-foreground">No alert events. Configure rules below.</p>
                : alertEvents.map(a => (
                    <div key={a.id} className="border border-border p-3 flex items-start justify-between gap-3" data-testid={`alert-row-${a.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                                <AlertTriangle className="w-3.5 h-3.5 text-amber-400"/>
                                <div className="text-sm text-foreground">{a.rule_name}</div>
                                <span className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">· {a.status}</span>
                            </div>
                            <div className="text-xs text-muted-foreground mt-1">{a.details}</div>
                            <div className="text-[10px] text-muted-foreground mt-1">IP {a.event_ip} · {a.event_user_email} · {fmtDateTime(a.created_at)}</div>
                        </div>
                        {a.status === "open" && <button onClick={() => resolveAlert(a.id)} data-testid={`alert-resolve-${a.id}`} className="text-[10px] text-emerald-400 hover:underline">Resolve</button>}
                    </div>
                ))}
            </div>
            <div className="bg-card border border-border p-6 space-y-2">
                <div className="overline">Recent login events</div>
                <FilterChip ctrl={loginCtrl}/>
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead><tr className="text-left text-[10px] uppercase tracking-[0.15em] text-muted-foreground border-b border-border">
                            <SortableTh field="created_at" ctrl={loginCtrl} className="py-2 px-3">When</SortableTh>
                            <SortableTh field="email" ctrl={loginCtrl} className="py-2 px-3">Email</SortableTh>
                            <SortableTh field="ip" ctrl={loginCtrl} className="py-2 px-3">IP</SortableTh>
                            <SortableTh field="result" ctrl={loginCtrl} className="py-2 px-3">Result</SortableTh>
                        </tr></thead>
                        <tbody>
                            {loginCtrl.rows.length === 0 ? <tr><td colSpan="4" className="py-4 text-center text-xs text-muted-foreground">No login events yet.</td></tr>
                            : loginCtrl.rows.map(e => (
                                <tr key={e.id} className="border-b border-border" data-testid={`login-event-${e.id}`}>
                                    <td className="py-2 px-3 text-muted-foreground text-xs">{fmtDateTime(e.created_at)}</td>
                                    <td className="py-2 px-3 text-foreground">{e.email}</td>
                                    <td className="py-2 px-3 text-muted-foreground text-xs">{e.ip || "—"}</td>
                                    <td className={`py-2 px-3 text-xs ${e.success ? "text-emerald-400" : "text-red-400"}`}>{e.success ? "● Success" : "○ Failed"}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

const Field = ({ label, children }) => (
    <div>
        <label className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mb-1.5 block">{label}</label>
        {children}
    </div>
);
const Input = (p) => (
    <input {...p} className="w-full bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary outline-none px-3 py-2 text-sm transition-colors" />
);

function b64ToBlob(b64, mime) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return new Blob([bytes], { type: mime });
}
